import { TRateScheduleSummary } from '@talon/web/lib/api';
import { useEstimateStore } from '@talon/web/src/context';
import { Box, Select, Text, Tip } from 'grommet';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

const AUTOMATIC = 'automatic';

// "SAG-AFTRA Commercials Contract 2025, Year 2" -> "2025 Year 2"
const shortLabel = (schedule?: TRateScheduleSummary) =>
  schedule?.label.replace(/^SAG-AFTRA Commercials Contract /, '').replace(',', '') ?? '';

export interface IContractSelectProps {
  /** Which date "Automatic" goes by */
  automaticBy: 'session date' | 'shoot date' | 'today’s date';
  /** The contract that priced the estimate (rate_schedule_id) */
  appliedId?: string | null;
  /** The contract a user chose (rate_schedule_override_id); null = automatic */
  overrideId?: string | null;
  onChange: (rateScheduleId: string | null) => Promise<unknown> | void;
  /** Whether it can be changed; by default, when the current estimate can be edited */
  editable?: boolean;
  /** Show an override as a small dot (with a tooltip) rather than a labelled badge, for tight rows */
  compact?: boolean;
  /** Leave out the "Contract" label, when a LabeledField around it gives one */
  hideLabel?: boolean;
}

/**
 * The contract (SAG-AFTRA rate sheet) a session or usage estimate is priced with: automatic by default,
 * or one the user chooses
 */
export const ContractSelect = observer(
  ({ automaticBy, appliedId, overrideId, onChange, editable, compact, hideLabel }: IContractSelectProps) => {
    const estimateStore = useEstimateStore();
    const [repricing, setRepricing] = useState(false);
    const canEdit = editable ?? estimateStore.canEditCurrent;

    useEffect(() => {
      estimateStore.loadRateSchedules();
    }, [estimateStore]);

    const schedules = estimateStore.rateSchedules;
    const applied = schedules.find((schedule) => schedule.id === appliedId);
    // Without an override the automatic contract is the one that priced it; with one, name the rule instead
    const automaticLabel = overrideId
      ? `Automatic (by ${automaticBy})`
      : applied
        ? `Automatic (${shortLabel(applied)})`
        : 'Automatic';
    const options = [
      { value: AUTOMATIC, label: automaticLabel },
      // Newest first
      ...[...schedules].reverse().map((schedule) => ({ value: schedule.id, label: shortLabel(schedule) })),
    ];
    const value = overrideId ?? AUTOMATIC;

    return (
      // Never squeezed (the rows it sits in are crowded); the badge wraps below when there's no room
      <Box direction="row" gap="xsmall" align="center" flex={false} wrap>
        {!hideLabel && <Text size="small">Contract</Text>}
        {canEdit ? (
          <Select
            size="small"
            style={{ minWidth: '13em' }}
            options={options}
            labelKey="label"
            valueKey={{ key: 'value', reduce: true }}
            value={value}
            disabled={repricing}
            onChange={async ({ value: next }) => {
              if (next === value) return;
              setRepricing(true);
              try {
                await onChange(next === AUTOMATIC ? null : next);
              } finally {
                setRepricing(false);
              }
            }}
          />
        ) : (
          <Text>{options.find((option) => option.value === value)?.label}</Text>
        )}
        {repricing && <Text size="xsmall">Repricing…</Text>}
        {overrideId && !repricing && (
          <Tip content={`Overridden: chosen instead of the contract in effect on the ${automaticBy}`}>
            {compact ? (
              <Box background="status-warning" round="full" width="10px" height="10px" flex={false} />
            ) : (
              <Box background="status-warning" round="xsmall" pad={{ horizontal: 'xsmall' }}>
                <Text size="xsmall">Overridden</Text>
              </Box>
            )}
          </Tip>
        )}
      </Box>
    );
  }
);
